import { useEffect, useRef, useState, useCallback } from "react";
import { Brain, Loader2, TrendingUp, TrendingDown } from "lucide-react";
import { useApp } from "@/context/AppContext";
import { useSocket } from "@/context/SocketContext";
import { resolveChatCompletionUrl } from "@/lib/llmDevProxy";
import { consumeChatCompletionStream } from "@/lib/streamChat";
import { formatUsdCompact } from "@/lib/formatUsd";

const SYSTEM_PROMPT = `You are an expert crypto trading analyst specialising in Solana pump.fun meme coins. 
You will be given a snapshot of open paper trade positions from an automated scalper bot, recent history, and live top runner data.

Analyse the data and respond with a concise, structured commentary. Cover:
1. **Portfolio Health** — overall floating PnL, win/loss ratio, concentration risk.
2. **Market Context** — what the top runners are doing right now (momentum/RoC) and if the bot should rotate into them.
3. **High Conviction Picks** — top performers in the open positions with reasoning.
4. **Risk Flags** — duplicate token names across different mints (ticker farming), tokens at dangerously high market caps (>$50M on pump.fun), very stale positions with no recent activity.
5. **Action Signals** — which positions you would tighten stops on, let run, or cut now.
6. **Auto Take Profits / Stops** — If you strongly believe a position should be closed immediately to lock in profits or cut severe losses, you MUST output EXACTLY \`[TAKE_PROFIT: <MINT_ADDRESS>]\` on its own line. The bot will automatically execute this sell.

Keep your response under 300 words. Use bullet points. Be direct and opinionated. 
Format using markdown with **bold** for emphasis. Start directly with your analysis, no preamble.`;

function buildPositionContext(positions: any[], history: any[] = [], topRunners: any[] = [], solPrice: number = 150): string {
  if (!positions || positions.length === 0) return "No open positions.";
  const sorted = [...positions].sort((a, b) => b.pnlPct - a.pnlPct);
  const totalFloatingUsd = sorted.reduce((acc, p) => acc + ((p.sizeUsd || 0) * (p.pnlPct || 0)), 0);
  const totalFloatingSol = totalFloatingUsd / solPrice;
  const winners = sorted.filter(p => p.pnlPct > 0).length;
  
  let ctx = `ACTIVE PAPER TRADE POSITIONS (${positions.length} open)\n`;
  ctx += `Floating PnL: ${totalFloatingSol >= 0 ? '+' : ''}${totalFloatingSol.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL | Winners: ${winners}/${positions.length}\n\n`;
  ctx += `Mint | Name | Entry MC | Live MC | PnL | Last Active\n`;
  ctx += `---|---|---|---|---|---\n`;
  sorted.forEach(p => {
    const meta = p.tokenMeta || {};
    const name = (meta.name || meta.symbol || 'Unknown').replace(/\|/g, '-');
    const entryMc = p.entryMcUsd ? formatUsdCompact(p.entryMcUsd) : '-';
    const liveMc = p.currentMcUsd ? formatUsdCompact(p.currentMcUsd) : '-';
    const pnl = p.pnlPct != null ? `${p.pnlPct > 0 ? '+' : ''}${(p.pnlPct * 100).toFixed(2)}%` : '-';
    const ageMs = p.lastActivity ? Date.now() - p.lastActivity : 0;
    const ageSec = Math.floor(ageMs / 1000);
    const ageStr = ageSec < 60 ? `${ageSec}s` : ageSec < 3600 ? `${Math.floor(ageSec/60)}m` : `${Math.floor(ageSec/3600)}h`;
    ctx += `${p.mint} | ${name} | ${entryMc} | ${liveMc} | ${pnl} | ${ageStr} ago\n`;
  });

  if (history && history.length > 0) {
    ctx += `\n\n--- RECENT CLOSED TRADES (Last 20) ---\n`;
    ctx += `Mint | Name | Entry MC | Exit MC | PnL | Duration | Alpha / Reason\n`;
    ctx += `---|---|---|---|---|---|---\n`;
    history.forEach(t => {
      const meta = t.tokenMeta || {};
      const name = (meta.name || meta.symbol || t.mint?.slice(0, 8) || 'Unknown').replace(/\|/g, '-');
      const entryMc = t.entryMcUsd ? formatUsdCompact(t.entryMcUsd) : '-';
      const exitMc = t.exitMcUsd ? formatUsdCompact(t.exitMcUsd) : '-';
      const pnl = t.pnlPct != null ? `${t.pnlPct > 0 ? '+' : ''}${(t.pnlPct * 100).toFixed(2)}%` : '-';
      
      const durationMs = t.entryTime ? (t.timestamp - t.entryTime) : 0;
      const durationSec = Math.floor(durationMs / 1000);
      const durationStr = durationSec < 60 ? `${durationSec}s` : durationSec < 3600 ? `${Math.floor(durationSec/60)}m` : `${Math.floor(durationSec/3600)}h`;
      
      const alpha = t.alpha || 'MANUAL';
      ctx += `${t.mint} | ${name} | ${entryMc} | ${exitMc} | ${pnl} | ${durationStr} | ${alpha}\n`;
    });
  }

  if (topRunners && topRunners.length > 0) {
    ctx += `\n\n--- LIVE MARKET TOP RUNNERS (Consider for next trades) ---\n`;
    ctx += `Mint | 1m RoC | 5m RoC | Vol / Reason\n`;
    ctx += `---|---|---|---\n`;
    topRunners.slice(0, 10).forEach(r => {
      const roc1m = r.roc1m ? `${r.roc1m > 0 ? '+' : ''}${(r.roc1m * 100).toFixed(1)}%` : '-';
      const roc5m = r.roc5m ? `${r.roc5m > 0 ? '+' : ''}${(r.roc5m * 100).toFixed(1)}%` : '-';
      const reason = r.waitReason || 'Momentum Buy';
      ctx += `${r.mint} | ${roc1m} | ${roc5m} | ${reason}\n`;
    });
  }

  return ctx;
}

function positionsFingerprint(positions: any[]): string {
  if (!positions || positions.length === 0) return "empty";
  return [...positions]
    .sort((a, b) => (a.mint || '').localeCompare(b.mint || ''))
    .map(p => `${p.mint}:${(p.pnlPct || 0).toFixed(2)}`)
    .join("|");
}

function hasSignificantChange(prev: any[], next: any[]): boolean {
  if (!prev || !next) return false;
  if (prev.length !== next.length) return true;
  const prevMap = new Map(prev.map(p => [p.mint, p.pnlPct]));
  for (const p of next) {
    const prevPnl = prevMap.get(p.mint);
    if (prevPnl === undefined) return true;
    if (Math.abs((p.pnlPct || 0) - (prevPnl || 0)) > 0.05) return true;
  }
  return false;
}

export function AiCommentaryPanel() {
  const { model, solPrice = 150 } = useApp() as any;
  const { serverOpenPositions, serverMomentumWatchlist } = useSocket();

  const [commentary, setCommentary] = useState<string>("");
  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [lastAnalyzedAt, setLastAnalyzedAt] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const prevPositionsRef = useRef<any[]>([]);
  const lastAnalyzeRef = useRef<number>(0);
  const abortRef = useRef<AbortController | null>(null);
  const MIN_AUTO_INTERVAL_MS = 3 * 60 * 1000;

  const runAnalysis = useCallback(async (positions: any[]) => {
    if (!model?.baseUrl || !model?.apiKey || !model?.model) {
      setError("No LLM configured. Set up your model in Settings.");
      return;
    }
    if (abortRef.current) abortRef.current.abort();
    abortRef.current = new AbortController();
    setIsAnalyzing(true);
    setError(null);
    setCommentary("");
    lastAnalyzeRef.current = Date.now();
    try {
      let history = [];
      try {
        const histRes = await fetch('/api/bot-history');
        if (histRes.ok) {
          const allHist = await histRes.json();
          history = allHist.slice(0, 20);
        }
      } catch (e) {
        console.warn("Failed to fetch history for AI:", e);
      }

      const url = resolveChatCompletionUrl(model.baseUrl);
      const res = await fetch(url, {
        method: "POST",
        signal: abortRef.current.signal,
        headers: {
          "Content-Type": "application/json",
          "Authorization": `Bearer ${model.apiKey}`,
        },
        body: JSON.stringify({
          model: model.model,
          stream: true,
          max_tokens: 600,
          messages: [
            { role: "system", content: SYSTEM_PROMPT },
            { role: "user", content: buildPositionContext(positions, history, serverMomentumWatchlist || [], solPrice) },
          ],
        }),
      });
      if (!res.ok) {
        const text = await res.text();
        throw new Error(`LLM error ${res.status}: ${text.slice(0, 200)}`);
      }
      
      let fullText = "";
      const executedTakes = new Set<string>();
      
      await consumeChatCompletionStream(res.body, (chunk) => {
        fullText += chunk;
        setCommentary(fullText);
        
        // Scan for execution commands on the fly
        const matches = fullText.matchAll(/\[TAKE_PROFIT:\s*([1-9A-HJ-NP-Za-km-z]{32,44})\]/g);
        for (const match of matches) {
          const mint = match[1];
          if (!executedTakes.has(mint)) {
            executedTakes.add(mint);
            console.log("🤖 AI triggering Auto Take Profit for", mint);
            fetch(`/api/bot-close/${mint}`, { method: 'POST' }).catch(e => console.error("AI execution failed:", e));
          }
        }
      });
      setLastAnalyzedAt(Date.now());
    } catch (err: any) {
      if (err.name !== "AbortError") setError(err.message || "Analysis failed");
    } finally {
      setIsAnalyzing(false);
    }
  }, [model]);

  const fingerprint = positionsFingerprint(serverOpenPositions || []);

  useEffect(() => {
    if (!serverOpenPositions || serverOpenPositions.length === 0) return;
    const now = Date.now();
    if (now - lastAnalyzeRef.current < MIN_AUTO_INTERVAL_MS) return;
    if (prevPositionsRef.current.length === 0 || hasSignificantChange(prevPositionsRef.current, serverOpenPositions)) {
      prevPositionsRef.current = serverOpenPositions;
      runAnalysis(serverOpenPositions);
    }
  }, [fingerprint]); // eslint-disable-line react-hooks/exhaustive-deps

  const formatElapsed = (ts: number | null) => {
    if (!ts) return null;
    const sec = Math.floor((Date.now() - ts) / 1000);
    if (sec < 60) return `${sec}s ago`;
    if (sec < 3600) return `${Math.floor(sec / 60)}m ago`;
    return `${Math.floor(sec / 3600)}h ago`;
  };

  const renderMarkdown = (text: string) => {
    return text.split('\n').map((line, i) => {
      if (line.startsWith('### ')) return <h4 key={i} className="text-[13px] font-black uppercase tracking-widest text-emerald-400 mt-4 mb-2">{line.slice(4)}</h4>;
      if (line.startsWith('## ')) return <h3 key={i} className="text-sm font-black uppercase tracking-widest text-white mt-4 mb-2">{line.slice(3)}</h3>;
      if (line.startsWith('# ')) return <h2 key={i} className="text-[15px] font-black text-white mt-4 mb-2">{line.slice(2)}</h2>;
      if (line.startsWith('- ') || line.startsWith('• ')) {
        const content = line.slice(2);
        return (
          <div key={i} className="flex items-start gap-2 text-[13px] text-white/90 leading-relaxed mb-1">
            <span className="text-purple-400 mt-1 shrink-0 text-lg leading-none">•</span>
            <span dangerouslySetInnerHTML={{ __html: content.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-black">$1</strong>') }} />
          </div>
        );
      }
      if (line.includes('[TAKE_PROFIT:')) {
        const mintMatch = line.match(/\[TAKE_PROFIT:\s*([^\]]+)\]/);
        return (
          <div key={i} className="flex items-center gap-1.5 mt-3 mb-2 p-2 bg-emerald-500/10 border border-emerald-500/20 rounded-lg">
             <Brain className="size-3 text-emerald-400" />
             <span className="text-[10px] font-black text-emerald-400 uppercase tracking-wider">AI Execution: Sent SELL signal for {mintMatch?.[1].substring(0,6)}...</span>
          </div>
        );
      }
      if (line.trim() === '') return <div key={i} className="h-2" />;
      return (
        <p key={i} className="text-[13px] text-white/90 leading-relaxed mb-2"
          dangerouslySetInnerHTML={{ __html: line.replace(/\*\*(.+?)\*\*/g, '<strong class="text-white font-black">$1</strong>') }}
        />
      );
    });
  };

  const totalFloatingUsd = (serverOpenPositions || []).reduce((acc: number, p: any) => acc + ((p.sizeUsd || 0) * (p.pnlPct || 0)), 0);
  const totalFloating = totalFloatingUsd / solPrice;
  const posCount = (serverOpenPositions || []).length;
  const winners = (serverOpenPositions || []).filter((p: any) => p.pnlPct > 0).length;

  return (
    <div className="bg-neutral-900 border border-purple-500/20 rounded-xl overflow-hidden shadow-[0_0_20px_rgba(168,85,247,0.06)]">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-white/5 bg-purple-500/5">
        <div className="flex items-center gap-2.5">
          <div className="size-7 rounded-full bg-purple-500/15 flex items-center justify-center">
            <Brain className="size-3.5 text-purple-400" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <span className="text-[10px] font-black uppercase tracking-widest text-purple-300">AI Portfolio Commentary</span>
              {isAnalyzing && (
                <span className="flex items-center gap-1 text-[9px] text-purple-400/60 animate-pulse">
                  <Loader2 className="size-2.5 animate-spin" /> Streaming...
                </span>
              )}
              {!isAnalyzing && lastAnalyzedAt && (
                <span className="text-[9px] text-white/20 font-mono">{formatElapsed(lastAnalyzedAt)}</span>
              )}
            </div>
            {posCount > 0 && (
              <div className="flex items-center gap-3 mt-0.5">
                <span className="text-[9px] font-mono text-white/25">{posCount} positions</span>
                <span className={`text-[9px] font-mono font-bold flex items-center gap-0.5 ${totalFloating >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
                  {totalFloating >= 0 ? <TrendingUp className="size-2.5" /> : <TrendingDown className="size-2.5" />}
                  {totalFloating >= 0 ? '+' : ''}{totalFloating.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} SOL
                </span>
                <span className="text-[9px] font-mono text-white/25">{winners}/{posCount} green</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Body */}
      <div className="px-4 py-3 min-h-[70px] max-h-[500px] overflow-y-auto custom-scrollbar">
        {error ? (
          <div className="text-[10px] text-rose-400 font-mono bg-rose-500/10 border border-rose-500/20 rounded-lg p-3">
            {error}
          </div>
        ) : commentary ? (
          <div className="space-y-0.5">
            {renderMarkdown(commentary)}
            {isAnalyzing && <span className="inline-block w-1.5 h-3 bg-purple-400/70 animate-pulse rounded-sm ml-0.5 align-middle" />}
          </div>
        ) : isAnalyzing ? (
          <div className="flex items-center gap-2 text-[10px] text-white/30 py-4 justify-center">
            <Loader2 className="size-3.5 animate-spin text-purple-400" />
            <span>Reading {posCount} positions...</span>
          </div>
        ) : posCount === 0 ? (
          <div className="text-[10px] text-white/20 py-4 text-center font-mono">No open positions yet.</div>
        ) : (
          <div className="text-[13px] text-white/40 py-4 text-center font-mono">
            Waiting for AI commentary...
          </div>
        )}
      </div>
    </div>
  );
}
